"use client";

import * as React from "react";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
export default function RelatedLinksAndEstimateForm() {
  const [formData, setFormData] = React.useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const relatedLinks = [{
    title: "Privacy Policy",
    to: "/privacy-policy"
  }, {
    title: "Contact",
    to: "/contact"
  }, {
    title: "About Us",
    to: "/about-us"
  }];
  const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
  const contactMethodOptions = ["Phone", "Text", "Email"];
  const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
  const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent before submitting.");
      return;
    }
    if (!formData.project_type) {
      setErrorMessage("Please select a project type.");
      return;
    }
    if (!formData.contact_method) {
      setErrorMessage("Please select a preferred contact method.");
      return;
    }
    if (!formData.timing) {
      setErrorMessage("Please select project timing.");
      return;
    }
    setIsLoading(true);
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");

      // Hidden fields
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", currentPath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section id="relatedlinksandestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Related Links Navigation */}
        <div className="mb-14 pb-10 border-b border-border/70">
          <nav aria-label="Related Links" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {relatedLinks.map((item, index) => <Link key={item.to} to={item.to} data-index={index} className="group flex items-center justify-between p-4 rounded-xl border border-border/60 bg-card/60 hover:bg-card hover:border-border transition-all shadow-xs hover:shadow-sm">
                <span className="font-serif text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                  {item.title}
                </span>
                <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </Link>)}
          </nav>
        </div>

        {/* Estimate Form Card */}
        <Card className="rounded-xl border border-border/80 bg-card/95 backdrop-blur-xs shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              Request a Project Estimate
            </CardTitle>
            <CardDescription className="font-sans text-muted-foreground text-base mt-2 max-w-lg mx-auto">
              Tell us about your home and remodeling ideas to receive a prompt consultation.
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {isSuccess ? <div className="py-12 px-4 text-center">
                <div className="inline-flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary mb-4">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
                  Request Received
                </h3>
                <p className="font-sans text-muted-foreground max-w-md mx-auto text-base">
                  Thank you for reaching out. We have received your project details and will be in touch shortly via your preferred contact method.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-6 font-sans">
                {errorMessage && <Alert variant="destructive" className="rounded-lg">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Notice</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-foreground">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={e => setFormData(prev => ({
                  ...prev,
                  name: e.target.value
                }))} className="bg-background" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="phone" name="phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={e => setFormData(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} className="bg-background" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-foreground">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))} className="bg-background" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium text-foreground">
                      Street Address or ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="address" name="address" type="text" required placeholder="123 Ponce de Leon Ave, Decatur, GA 30030" value={formData.address} onChange={e => setFormData(prev => ({
                  ...prev,
                  address: e.target.value
                }))} className="bg-background" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="project_type" className="text-sm font-medium text-foreground">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.project_type} onValueChange={val => setFormData(prev => ({
                  ...prev,
                  project_type: val
                }))}>
                      <SelectTrigger id="project_type" className="w-full bg-background">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypeOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_method" className="text-sm font-medium text-foreground">
                      Preferred Contact Method <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.contact_method} onValueChange={val => setFormData(prev => ({
                  ...prev,
                  contact_method: val
                }))}>
                      <SelectTrigger id="contact_method" className="w-full bg-background">
                        <SelectValue placeholder="Select contact method" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactMethodOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="timing" className="text-sm font-medium text-foreground">
                      Project Timing <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.timing} onValueChange={val => setFormData(prev => ({
                  ...prev,
                  timing: val
                }))}>
                      <SelectTrigger id="timing" className="w-full bg-background">
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        {timingOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budget" className="text-sm font-medium text-foreground">
                      Estimated Budget (Optional)
                    </Label>
                    <Select value={formData.budget} onValueChange={val => setFormData(prev => ({
                  ...prev,
                  budget: val
                }))}>
                      <SelectTrigger id="budget" className="w-full bg-background">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="details" className="text-sm font-medium text-foreground">
                    Project Details (Optional)
                  </Label>
                  <Textarea id="details" name="details" rows={4} placeholder="Describe your project scope, goals, or any specific ideas..." value={formData.details} onChange={e => setFormData(prev => ({
                ...prev,
                details: e.target.value
              }))} className="bg-background resize-y" />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => setFormData(prev => ({
                ...prev,
                consent: checked === true
              }))} className="mt-1" required />
                  <Label htmlFor="consent" className="text-sm leading-snug text-muted-foreground cursor-pointer font-normal">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>

                <div className="pt-3">
                  <Button type="submit" disabled={isLoading} className="w-full h-11 text-base font-medium rounded-md shadow-sm">
                    {isLoading ? <>
                        <Loader2 className="size-4 animate-spin mr-2" />
                        Submitting Request...
                      </> : "Submit Estimate Request"}
                  </Button>
                </div>
              </form>}
          </CardContent>
        </Card>
      </div>
    </section>;
}